import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useContext} from "react";

import {addStyle} from "@khanacademy/wonder-blocks-core";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {usePrefersReducedMotion} from "@khan/data-prefers-reduced-motion";
import {useCoeditorContext} from "@khan/guide-coeditor-core";
import {useStopGuideResponse} from "@khan/guide-core";
import {maybeIOS} from "@khan/guide-core-helpers";
import {useGuideState} from "@khan/guide-state";
import {
    ActionStatusStreamAction,
    ActionStatusStreamStatus,
    type ChatInputMetadata,
    type Conversation,
    type HideChatOptions,
    type InteractionID,
    type Message,
} from "@khan/guide-types";
import {
    CHAT_INPUT_EXPANDED_PADDING,
    getGuideIconReaction,
} from "@khan/guide-utils";

import {PreviewModeContext} from "../PreviewModeContext.ts";
import {SystemMessage} from "../system-message.tsx";

import {MessageBubble} from "./message-bubble.tsx";
import {PrefetchMessageBubbleIcons} from "./prefetch-message-bubble-icons.tsx";
import {useImagesMap} from "./use-images-map.ts";
import type {OpenModalActionButtonCallback} from "./use-make-action-bubble-handlers.ts";

interface Props {
    messagesWithMathCheck: Set<number>;
    conversation: Conversation;
    hideChatOptions?: HideChatOptions;
    expanded?: boolean;
    actionBubbleCallbacks: {
        onMessageSend: (
            message: string,
            metadata: ChatInputMetadata,
        ) => unknown;
        openModal: OpenModalActionButtonCallback;
    };
    onOpenFeedbackForm: (interactionId: InteractionID) => unknown;
}

const StyledUl = addStyle("ul");

const noop = () => {};
const actionBubbleNoop = {onMessageSend: noop, openModal: noop};

const isMobile: any = "@media screen and (max-width: 450px)";

export const Messages = ({
    messagesWithMathCheck,
    conversation,
    expanded,
    actionBubbleCallbacks,
    onOpenFeedbackForm,
    hideChatOptions,
}: Props): React.ReactElement => {
    const uploadedImagesMap = useImagesMap();
    const stopResponse = useStopGuideResponse();
    const prefersReducedMotion = usePrefersReducedMotion();
    const hasAcknowledgedGuide = useGuideState("hasAcknowledgedGuide");
    const isLoading = useGuideState("isLoading");
    const bufferResponse = useGuideState("bufferResponse");
    const stopId = useGuideState("stopId");
    const {isKhanmigoTyping: isTypingInCoeditor} = useCoeditorContext();

    const lastIncomingIndex = conversation.findLastIndex(
        (message) => message.type === "incoming",
    );

    const lastNonSystemMessageIndex = conversation.findLastIndex(
        (message) => message.type !== "system",
    );

    const firstOutgoingMessageIndex = conversation.findIndex(
        (message) => message.type === "outgoing",
    );

    const isInitialMessageGroup = (index: number) =>
        firstOutgoingMessageIndex === -1 || index < firstOutgoingMessageIndex;

    // For initial message groups, several messages share the same interaction ID.
    // However, we need unique message keys to stabilize the component tree for
    // DAB calls to be made. After the initial message group, messages are
    // unique, so we provide unique keys after the first outgoing message.
    const getKey = (message: Message, index: number) =>
        message.interactionId && !isInitialMessageGroup(index)
            ? [message.interactionId, message.type].join("-")
            : index;

    const previewModeContextValue = useContext(PreviewModeContext);
    const isPreviewMode =
        previewModeContextValue !== undefined &&
        previewModeContextValue["isPreviewMode"];
    return (
        <>
            <StyledUl
                style={[
                    styles.messages,
                    expanded && styles.expanded,
                    maybeIOS() && styles.isIOS,
                    isPreviewMode && styles.previewMode,
                ]}
            >
                {conversation.map((message, index) => {
                    const isIncoming = message.type === "incoming";
                    const isOutgoing = message.type === "outgoing";
                    const isLastMessageInConversation =
                        index === conversation.length - 1;
                    const isLastNonSystemMessageInConversation =
                        index === lastNonSystemMessageIndex;
                    const isLastIncomingResponseInResponsesGroup =
                        isIncoming &&
                        (index === lastIncomingIndex ||
                            conversation[index + 1].type !== "incoming");

                    // If the TEXT action is LOADING, we are currently streaming text
                    const isStreamingText =
                        message.actionStatusMap?.get(
                            ActionStatusStreamAction.TEXT,
                        ) === ActionStatusStreamStatus.LOADING;

                    // isSending is synonymous with isLoading when we are buffering
                    // responses. In the streaming case, we are sending only when we're
                    // loading, not typing AND the last message is outgoing (there can
                    // be a split second where we are loading and finished typing but
                    // haven't completed the state update to set loading false, checking
                    // that the last message is outgoing will prevent a janky flicker).
                    const isSending =
                        isLoading &&
                        isOutgoing &&
                        isLastMessageInConversation &&
                        (bufferResponse || // buffer case
                            !isTypingInCoeditor); // streaming case

                    // A user can stop the generation of a message if it's not
                    // an error and if we have a stop ID. We display the stop
                    // response button on an incoming message if the message is
                    // generating a response & the stop ID matches the interaction.
                    // In rare cases, we'll display a stop response button on a
                    // pending outgoing message if Khanmigo is typing in the
                    // coeditor and there is no incoming message from Khanmigo yet.
                    const canBeStopped =
                        !!stopId &&
                        !message.isError &&
                        ((isIncoming &&
                            message.isGeneratingResponse &&
                            stopId === message.interactionId &&
                            isLastIncomingResponseInResponsesGroup) ||
                            (isOutgoing &&
                                isTypingInCoeditor &&
                                message.interactionId === "" &&
                                isLastMessageInConversation));

                    return message.type === "system" ? (
                        <SystemMessage key={index} content={message.text} />
                    ) : (
                        <MessageBubble
                            key={getKey(message, index)}
                            imagesMap={uploadedImagesMap}
                            message={message}
                            // TODO: Refactor to be more explicit about how the
                            // component should change in response to the prop.
                            // i.e. `isFirstIncomingResponseInResponsesGroup` could
                            // become `userAvatar` and MessageBubble would render
                            // an avatar if it is provided.
                            isFirstIncomingResponseInResponsesGroup={
                                isIncoming &&
                                (index === 0 ||
                                    conversation[index - 1].type !== "incoming")
                            }
                            // TODO: Refactor to be more explicit about how the
                            // component should change in response to the prop.
                            // i.e. `isLastIncomingResponseInResponsesGroup` could
                            // become `showFeedbackFooter`.
                            isLastIncomingResponseInResponsesGroup={
                                isLastIncomingResponseInResponsesGroup
                            }
                            // TODO: Refactor to be more explicit about how the
                            // component should change in response to the prop.
                            // Components shouldn't need to know about its context,
                            // parents with contextual info can use that to drive
                            // the state of the component.
                            isMostRecentMessage={isLastMessageInConversation}
                            isMostRecentNonSystemMessage={
                                isLastNonSystemMessageInConversation
                            }
                            isSending={isSending}
                            isTypingInChat={
                                isStreamingText &&
                                isLastIncomingResponseInResponsesGroup
                            }
                            isTypingInCoeditor={
                                isLastMessageInConversation &&
                                isTypingInCoeditor
                            }
                            showExpandedIcon={expanded}
                            actionBubbleCallbacks={actionBubbleCallbacks}
                            onLeaveFeedback={onOpenFeedbackForm}
                            stopResponse={stopResponse}
                            canBeStopped={canBeStopped}
                            reaction={
                                !prefersReducedMotion &&
                                index === lastIncomingIndex
                                    ? getGuideIconReaction(message)
                                    : null
                            }
                            isInitialMessage={isInitialMessageGroup(index)}
                            hadMathCheck={messagesWithMathCheck.has(index)}
                        />
                    );
                })}

                {hideChatOptions?.message && hasAcknowledgedGuide && (
                    <MessageBubble
                        key="disabled"
                        canBeStopped={false}
                        message={{
                            text: hideChatOptions.message,
                            type: "incoming",
                            interactionId: "",
                            isError: true,
                            uiOnly: true,
                            user: "Teacher",
                            serverOnly: false,
                        }}
                        reaction={null}
                        showExpandedIcon={expanded}
                        stopResponse={stopResponse}
                        actionBubbleCallbacks={actionBubbleNoop}
                        onLeaveFeedback={noop}
                    />
                )}
            </StyledUl>
            {/* We render hidden icons that need to be displayed
                under poor network conditions so that the assets are
                available when we do intend to show them. */}
            <PrefetchMessageBubbleIcons />
        </>
    );
};

const styles = StyleSheet.create({
    messages: {
        position: "relative",
        display: "flex",
        flexDirection: "column",
        listStyle: "none",
        background: color.white,
        borderRadius: 8,
        color: color.offBlack,
        paddingBlockStart: spacing.large_24,
        paddingBlockEnd: spacing.large_24,
        gap: spacing.large_24,
    },
    isIOS: {
        overflowY: "scroll",
    },
    expanded: {
        // As designed to match chat input and left side padding.
        paddingInlineEnd: `${CHAT_INPUT_EXPANDED_PADDING}px`,
    },
    previewMode: {
        height: "100%",
        overflowY: "auto",
        paddingInlineStart: spacing.medium_16,
        [isMobile]: {
            paddingRight: spacing.medium_16,
        },
    },
});
